import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, User, MapPin, Building, Check, X, Settings, AlertCircle, Pencil } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Unified Client Form Component
 * Used across the entire application for client creation/editing
 * Single source of truth for client data entry
 */
const ClientForm = ({ 
  open, 
  onOpenChange, 
  editClient = null, 
  onSuccess,
  getAuthHeader 
}) => {
  const [formData, setFormData] = useState({
    project_brand_name: '',
    name: '',
    client_code: '',
    billing_address: '',
    gst_number: '',
    city: '',
    contact1_name: '',
    contact1_designation: '',
    contact1_phone: '',
    contact1_email: '',
    contact2_name: '',
    contact2_designation: '',
    contact2_phone: '',
    contact2_email: '',
    delivery_addresses: []
  });

  const [newDeliveryAddress, setNewDeliveryAddress] = useState({
    address: '',
    city: '',
    state: '',
    pincode: ''
  });

  // Client code validation state
  const [clientCodeStatus, setClientCodeStatus] = useState({ valid: null, available: null, suggestions: [] });
  const [clientCodeLoading, setClientCodeLoading] = useState(false);
  
  // Client name validation state
  const [clientNameStatus, setClientNameStatus] = useState({ available: null });
  const [clientNameLoading, setClientNameLoading] = useState(false);
  
  // City codes state
  const [cityCodes, setCityCodes] = useState([]);
  const [showCityManager, setShowCityManager] = useState(false);
  const [newCityName, setNewCityName] = useState('');
  const [newCityCode, setNewCityCode] = useState('');
  const [editingCityId, setEditingCityId] = useState(null);
  const [editCityCodeValue, setEditCityCodeValue] = useState('');
  const [cityCodeSaving, setCityCodeSaving] = useState(false);

  // Client codes state (for Manage Client Codes dialog)
  const [clientCodes, setClientCodes] = useState([]);
  const [showClientCodeManager, setShowClientCodeManager] = useState(false);
  const [clientCodeWarning, setClientCodeWarning] = useState(null);

  // Fetch city codes on mount
  useEffect(() => {
    if (open) {
      fetchCityCodes();
      fetchClientCodes();
    }
  }, [open]);

  const fetchCityCodes = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients/city-codes`, { headers: getAuthHeader() });
      setCityCodes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch city codes:', error);
    }
  };

  const fetchClientCodes = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients/client-codes`, { headers: getAuthHeader() });
      setClientCodes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch client codes:', error);
    }
  };

  const resetFormData = () => {
    setFormData({
      project_brand_name: '',
      name: '',
      client_code: '',
      billing_address: '',
      gst_number: '',
      city: '',
      contact1_name: '',
      contact1_designation: '',
      contact1_phone: '',
      contact1_email: '',
      contact2_name: '',
      contact2_designation: '',
      contact2_phone: '',
      contact2_email: '',
      delivery_addresses: []
    });
    setNewDeliveryAddress({ address: '', city: '', state: '', pincode: '' });
    setClientCodeStatus({ valid: null, available: null, suggestions: [] });
    setClientNameStatus({ available: null });
  };

  // Reset form when dialog opens/closes or editClient changes
  useEffect(() => {
    if (open) {
      if (editClient) {
        // Extract 3-letter client code from stored code (first 3 letters)
        const storedCode = editClient.client_code || '';
        const clientCode3 = storedCode.length >= 3 ? storedCode.substring(0, 3) : '';
        
        setFormData({
          project_brand_name: editClient.project_brand_name || '',
          name: editClient.name || '',
          client_code: clientCode3,
          billing_address: editClient.billing_address || '',
          gst_number: editClient.gst_number || '',
          city: editClient.city || '',
          contact1_name: editClient.contact1?.name || '',
          contact1_designation: editClient.contact1?.designation || '',
          contact1_phone: editClient.contact1?.phone || '',
          contact1_email: editClient.contact1?.email || '',
          contact2_name: editClient.contact2?.name || '',
          contact2_designation: editClient.contact2?.designation || '',
          contact2_phone: editClient.contact2?.phone || '',
          contact2_email: editClient.contact2?.email || '',
          delivery_addresses: editClient.delivery_addresses || []
        });
        // Mark as valid for existing clients
        if (clientCode3) {
          setClientCodeStatus({ valid: true, available: true, suggestions: [] });
        }
      } else {
        resetFormData();
      }
    }
  }, [open, editClient]);

  // Validate client code when it changes
  const validateClientCode = useCallback(async (code) => {
    if (!code || code.length < 3) {
      setClientCodeStatus({ valid: false, available: null, suggestions: [] });
      setClientCodeWarning(null);
      return;
    }
    
    if (code.length !== 3 || !/^[A-Za-z]{3}$/.test(code)) {
      setClientCodeStatus({ valid: false, available: null, suggestions: [], error: 'Must be exactly 3 letters' });
      setClientCodeWarning(null);
      return;
    }
    
    // Skip validation if editing and code hasn't changed
    if (editClient) {
      const existingCode = (editClient.client_code || '').substring(0, 3).toUpperCase();
      if (code.toUpperCase() === existingCode) {
        setClientCodeStatus({ valid: true, available: true, suggestions: [] });
        setClientCodeWarning(null);
        return;
      }
    }
    
    setClientCodeLoading(true);
    try {
      const response = await axios.get(`${API_URL}/clients/validate-client-code/${code}`, { headers: getAuthHeader() });
      // Allow duplicate 3-letter codes with a warning
      if (response.data.available === false && response.data.existing_client) {
        // Show warning but mark as valid (user can proceed)
        setClientCodeStatus({ ...response.data, valid: true, available: true });
        setClientCodeWarning(`Code "${code}" is already used by "${response.data.existing_client}". Use the same code only if this is a related location/branch.`);
      } else {
        setClientCodeStatus(response.data);
        setClientCodeWarning(null);
      }
    } catch (error) {
      setClientCodeStatus({ valid: false, available: null, suggestions: [] });
      setClientCodeWarning(null);
    } finally {
      setClientCodeLoading(false);
    }
  }, [editClient, getAuthHeader]);

  // Debounced validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.client_code) {
        validateClientCode(formData.client_code);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.client_code, validateClientCode]);

  // Validate client name when it changes
  const validateClientName = useCallback(async (name) => {
    if (!name || name.trim().length < 2) {
      setClientNameStatus({ available: null });
      return;
    }
    
    // Skip validation if editing and name hasn't changed
    if (editClient) {
      if (name.trim().toLowerCase() === (editClient.name || '').toLowerCase()) {
        setClientNameStatus({ available: true });
        return;
      }
    }
    
    setClientNameLoading(true);
    try {
      const excludeId = editClient?.id || '';
      const response = await axios.get(
        `${API_URL}/clients/validate-client-name?name=${encodeURIComponent(name)}${excludeId ? `&exclude_id=${excludeId}` : ''}`,
        { headers: getAuthHeader() }
      );
      setClientNameStatus(response.data);
    } catch (error) {
      setClientNameStatus({ available: null });
    } finally {
      setClientNameLoading(false);
    }
  }, [editClient, getAuthHeader]);

  // Debounced name validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.name) {
        validateClientName(formData.name);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.name, validateClientName]);

  // Add new city code
  const handleAddCityCode = async () => {
    if (!newCityName.trim() || !newCityCode.trim()) {
      toast.error('Both city name and code are required');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/clients/city-codes?city=${encodeURIComponent(newCityName)}&code=${encodeURIComponent(newCityCode)}`, {}, { headers: getAuthHeader() });
      toast.success(`City code added: ${newCityName} = ${newCityCode}`);
      setNewCityName('');
      setNewCityCode('');
      setShowCityManager(false);
      fetchCityCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add city code');
    }
  };

  const handleEditCityCode = async (cityId) => {
    const code = editCityCodeValue.trim().toUpperCase();
    if (!code || code.length !== 2 || !/^[A-Z]{2}$/.test(code)) {
      toast.error('City code must be exactly 2 letters');
      return;
    }
    setCityCodeSaving(true);
    try {
      const res = await axios.patch(
        `${API_URL}/clients/city-codes/${cityId}?new_code=${encodeURIComponent(code)}`,
        {},
        { headers: getAuthHeader() }
      );
      const d = res.data;
      toast.success(`${d.message} — Updated ${d.updated_clients} client(s), ${d.updated_addresses} address(es)`);
      setEditingCityId(null);
      setEditCityCodeValue('');
      fetchCityCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update city code');
    } finally {
      setCityCodeSaving(false);
    }
  };

  // Get city code for a city
  const getCityCodeForCity = (cityName) => {
    const cityUpper = (cityName || '').toUpperCase();
    const found = cityCodes.find(c => c.city === cityUpper);
    return found ? found.code : null;
  };

  const addDeliveryAddress = () => {
    if (!newDeliveryAddress.city.trim()) {
      toast.error('City is required for delivery address');
      return;
    }
    
    // Get city code from master, or show warning to add it
    const cityCode = getCityCodeForCity(newDeliveryAddress.city);
    if (!cityCode) {
      toast.warning(`City "${newDeliveryAddress.city}" not in master. Please add it via Manage Cities.`);
      setShowCityManager(true);
      setNewCityName(newDeliveryAddress.city.toUpperCase());
      return;
    }
    
    const newAddr = {
      ...newDeliveryAddress,
      city: newDeliveryAddress.city.toUpperCase(),
      city_code: cityCode,
      is_default: formData.delivery_addresses.length === 0
    };
    
    setFormData({
      ...formData,
      delivery_addresses: [...formData.delivery_addresses, newAddr]
    });
    setNewDeliveryAddress({ address: '', city: '', state: '', pincode: '' });
  };

  const removeDeliveryAddress = (index) => {
    const updated = formData.delivery_addresses.filter((_, i) => i !== index);
    // Ensure at least one is default
    if (updated.length > 0 && !updated.some(a => a.is_default)) {
      updated[0].is_default = true;
    }
    setFormData({ ...formData, delivery_addresses: updated });
  };

  const setDefaultAddress = (index) => {
    const updated = formData.delivery_addresses.map((a, i) => ({
      ...a,
      is_default: i === index
    }));
    setFormData({ ...formData, delivery_addresses: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Client name is required');
      return;
    }
    
    // Check if name is available
    if (clientNameStatus.available === false) {
      toast.error('Client name is already taken. Please choose another.');
      return;
    }
    
    // Validate client code for new clients
    if (!editClient && !formData.client_code) {
      toast.error('Client code is required (3 letters)');
      return;
    }
    
    if (formData.client_code && (formData.client_code.length !== 3 || !/^[A-Za-z]{3}$/.test(formData.client_code))) {
      toast.error('Client code must be exactly 3 letters');
      return;
    }
    
    if (!editClient && clientCodeStatus.available === false) {
      toast.error('Client code is already taken. Please choose another.');
      return;
    }

    try {
      if (editClient) {
        await axios.patch(`${API_URL}/clients/${editClient.id}`, formData, { headers: getAuthHeader() });
        toast.success('Client updated successfully');
      } else {
        await axios.post(`${API_URL}/clients`, formData, { headers: getAuthHeader() });
        toast.success('Client created successfully');
      }
      
      resetFormData();
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save client');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Building className="w-5 h-5" />
            {editClient ? 'Edit Client' : 'Add New Client'}
          </DialogTitle>
          <DialogDescription>
            {editClient ? 'Update client information' : 'Enter client details to create a new client'}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto space-y-6 py-4 pr-2">
            
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2 text-sm">
                <Building className="w-4 h-4" /> Client Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client Name *</Label>
                  <div className="relative">
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Company / Client name"
                      required
                      className={`${
                        clientNameStatus.available === true ? 'border-green-500 pr-8' :
                        clientNameStatus.available === false ? 'border-red-500 pr-8' : ''
                      }`}
                    />
                    {clientNameLoading && (
                      <div className="absolute right-2 top-1/2 -translate-y-1/2">
                        <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                      </div>
                    )}
                    {!clientNameLoading && clientNameStatus.available === true && (
                      <Check className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                    )}
                    {!clientNameLoading && clientNameStatus.available === false && (
                      <X className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />
                    )}
                  </div>
                  {clientNameStatus.available === false && (
                    <div className="text-xs text-red-600">
                      Name already taken by: {clientNameStatus.existing_client}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Client Code * (3 letters)</Label>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 text-xs"
                      onClick={() => setShowClientCodeManager(true)}
                    >
                      <Settings className="w-3 h-3 mr-1" />
                      Manage
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input
                        value={formData.client_code}
                        onChange={(e) => setFormData({ ...formData, client_code: e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3) })}
                        placeholder="ABC"
                        maxLength={3}
                        className={`uppercase ${
                          clientCodeStatus.available === true ? 'border-green-500 pr-8' :
                          clientCodeStatus.available === false ? 'border-red-500 pr-8' : ''
                        }`}
                        disabled={false}
                      />
                      {clientCodeLoading && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2">
                          <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                        </div>
                      )}
                      {!clientCodeLoading && clientCodeStatus.available === true && (
                        <Check className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                      )}
                      {!clientCodeLoading && clientCodeStatus.available === false && (
                        <X className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />
                      )}
                    </div>
                  </div>
                  {/* Warning for duplicate 3-letter code (user can still proceed) */}
                  {clientCodeWarning && (
                    <div className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {clientCodeWarning}
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Project / Brand Name</Label>
                  <Input
                    value={formData.project_brand_name}
                    onChange={(e) => setFormData({ ...formData, project_brand_name: e.target.value })}
                    placeholder="Project or brand name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>GST Number</Label>
                  <Input
                    value={formData.gst_number}
                    onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })}
                    placeholder="22AAAAA0000A1Z5"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>City</Label>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 text-xs"
                      onClick={() => setShowCityManager(true)}
                    >
                      <Settings className="w-3 h-3 mr-1" />
                      Manage Cities
                    </Button>
                  </div>
                  <Select 
                    value={formData.city} 
                    onValueChange={(val) => setFormData({ ...formData, city: val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select city" />
                    </SelectTrigger>
                    <SelectContent>
                      {cityCodes.map(cc => (
                        <SelectItem key={cc.city} value={cc.city}>
                          {cc.city} ({cc.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>SO Code Preview</Label>
                  <div className="h-9 px-3 py-2 rounded-md border bg-muted/50 text-sm font-mono">
                    {formData.client_code || '___'}
                    {getCityCodeForCity(formData.city) || '__'}
                    0001A
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Billing Address</Label>
                <Input
                  value={formData.billing_address}
                  onChange={(e) => setFormData({ ...formData, billing_address: e.target.value })}
                  placeholder="Full billing address"
                />
              </div>
            </div>

            {/* Contact Information */}
            <div className="border-t pt-4 space-y-4">
              <h3 className="font-semibold flex items-center gap-2 text-sm">
                <User className="w-4 h-4" /> Contact Information
              </h3>
              <div className="grid grid-cols-2 gap-6">
                {/* Contact 1 */}
                <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                  <p className="font-medium text-sm">Contact 1 (Primary)</p>
                  <Input
                    value={formData.contact1_name}
                    onChange={(e) => setFormData({ ...formData, contact1_name: e.target.value })}
                    placeholder="Contact Name"
                  />
                  <Input
                    value={formData.contact1_designation}
                    onChange={(e) => setFormData({ ...formData, contact1_designation: e.target.value })}
                    placeholder="Designation (e.g., Manager, Director)"
                  />
                  <Input
                    value={formData.contact1_phone}
                    onChange={(e) => setFormData({ ...formData, contact1_phone: e.target.value })}
                    placeholder="Phone Number"
                  />
                  <Input
                    type="email"
                    value={formData.contact1_email}
                    onChange={(e) => setFormData({ ...formData, contact1_email: e.target.value })}
                    placeholder="Email Address"
                  />
                </div>
                
                {/* Contact 2 */}
                <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                  <p className="font-medium text-sm">Contact 2 (Secondary)</p>
                  <Input
                    value={formData.contact2_name}
                    onChange={(e) => setFormData({ ...formData, contact2_name: e.target.value })}
                    placeholder="Contact Name"
                  />
                  <Input
                    value={formData.contact2_designation}
                    onChange={(e) => setFormData({ ...formData, contact2_designation: e.target.value })}
                    placeholder="Designation (e.g., Manager, Director)"
                  />
                  <Input
                    value={formData.contact2_phone}
                    onChange={(e) => setFormData({ ...formData, contact2_phone: e.target.value })}
                    placeholder="Phone Number"
                  />
                  <Input
                    type="email"
                    value={formData.contact2_email}
                    onChange={(e) => setFormData({ ...formData, contact2_email: e.target.value })}
                    placeholder="Email Address"
                  />
                </div>
              </div>
            </div>

            {/* Delivery Addresses */}
            <div className="border-t pt-4 space-y-4">
              <h3 className="font-semibold flex items-center gap-2 text-sm">
                <MapPin className="w-4 h-4" /> Delivery Addresses
              </h3>
              
              {/* Existing Addresses */}
              {formData.delivery_addresses.length > 0 && (
                <div className="space-y-2">
                  {formData.delivery_addresses.map((addr, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg">
                      <input
                        type="radio"
                        name="defaultAddress"
                        checked={addr.is_default}
                        onChange={() => setDefaultAddress(idx)}
                        className="w-4 h-4"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{addr.city} {addr.is_default && <span className="text-xs text-green-600">(Default)</span>}</p>
                        <p className="text-xs text-muted-foreground">
                          {addr.address}{addr.state ? `, ${addr.state}` : ''}{addr.pincode ? ` - ${addr.pincode}` : ''}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => removeDeliveryAddress(idx)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add New Address */}
              <div className="p-4 border rounded-lg space-y-3">
                <p className="text-sm font-medium">Add Delivery Address</p>
                <div className="grid grid-cols-4 gap-3">
                  <div className="col-span-2">
                    <Input
                      value={newDeliveryAddress.address}
                      onChange={(e) => setNewDeliveryAddress({ ...newDeliveryAddress, address: e.target.value })}
                      placeholder="Street Address"
                    />
                  </div>
                  <Input
                    value={newDeliveryAddress.city}
                    onChange={(e) => setNewDeliveryAddress({ ...newDeliveryAddress, city: e.target.value })}
                    placeholder="City *"
                  />
                  <Input
                    value={newDeliveryAddress.state}
                    onChange={(e) => setNewDeliveryAddress({ ...newDeliveryAddress, state: e.target.value })}
                    placeholder="State"
                  />
                </div>
                <div className="flex gap-3">
                  <Input
                    value={newDeliveryAddress.pincode}
                    onChange={(e) => setNewDeliveryAddress({ ...newDeliveryAddress, pincode: e.target.value })}
                    placeholder="Pincode"
                    className="w-32"
                  />
                  <Button type="button" variant="outline" onClick={addDeliveryAddress}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Address
                  </Button>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex-shrink-0 border-t pt-4 mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!editClient && clientCodeStatus.available === false}>
              {editClient ? 'Update Client' : 'Create Client'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      
      {/* Manage Client Codes Dialog */}
      <Dialog open={showClientCodeManager} onOpenChange={setShowClientCodeManager}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Manage Client Codes
            </DialogTitle>
            <DialogDescription>
              View all 3-letter client codes. Duplicate codes are allowed but highlighted.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Group codes by 3-letter prefix */}
            {(() => {
              // Group clients by their 3-letter code prefix
              const grouped = {};
              clientCodes.forEach(c => {
                const prefix = c.client_code_3 || (c.client_code || '').substring(0, 3).toUpperCase();
                if (!prefix) return;
                if (!grouped[prefix]) grouped[prefix] = [];
                grouped[prefix].push(c);
              });
              
              const sortedPrefixes = Object.keys(grouped).sort();
              
              if (sortedPrefixes.length === 0) {
                return (
                  <div className="p-4 text-center text-muted-foreground text-sm">
                    No client codes defined yet
                  </div>
                );
              }
              
              return (
                <div className="max-h-80 overflow-y-auto border rounded-lg divide-y">
                  {sortedPrefixes.map(prefix => {
                    const clients = grouped[prefix];
                    const isDuplicate = clients.length > 1;
                    
                    return (
                      <div key={prefix} className={`px-3 py-2 ${isDuplicate ? 'bg-amber-50' : ''}`}>
                        <div className="flex items-center justify-between">
                          <Badge 
                            variant={isDuplicate ? "outline" : "secondary"} 
                            className={`font-mono ${isDuplicate ? 'border-amber-500 text-amber-700' : ''}`}
                          >
                            {prefix}
                          </Badge>
                          {isDuplicate && (
                            <span className="text-xs text-amber-600 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Shared by {clients.length} clients
                            </span>
                          )}
                        </div>
                        <div className="mt-1 space-y-0.5">
                          {clients.map((c, idx) => (
                            <div key={c.id || idx} className="text-sm flex items-center gap-2">
                              <span className="text-muted-foreground">{c.name}</span>
                              <span className="text-xs text-muted-foreground font-mono">
                                ({c.client_code})
                              </span>
                              {c.city && (
                                <span className="text-xs text-muted-foreground">
                                  — {c.city}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            
            <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg">
              <p className="font-medium mb-1">Client Code Rules:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>3-letter codes (e.g., FRN) can be shared across clients with a warning</li>
                <li>Full 5-letter codes (e.g., FRNVD) must be unique per client</li>
                <li>The 5-letter code = 3-letter client code + 2-letter city code</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClientCodeManager(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* City Manager Dialog */}
      <Dialog open={showCityManager} onOpenChange={setShowCityManager}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Manage City Codes
            </DialogTitle>
            <DialogDescription>
              Add new cities and their 2-letter codes for SO numbering
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Existing City Codes */}
            <div className="max-h-48 overflow-y-auto border rounded-lg">
              {cityCodes.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground text-sm">
                  No city codes defined yet
                </div>
              ) : (
                <div className="divide-y">
                  {cityCodes.map(cc => (
                    <div key={cc.city} className="flex justify-between items-center px-3 py-2 gap-2">
                      <span className="text-sm">{cc.city}</span>
                      {editingCityId === cc.id ? (
                        <div className="flex items-center gap-1">
                          <Input
                            value={editCityCodeValue}
                            onChange={(e) => setEditCityCodeValue(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2))}
                            className="w-16 h-7 uppercase font-mono text-xs px-1"
                            maxLength={2}
                            autoFocus
                            onKeyDown={(e) => { if (e.key === 'Enter') handleEditCityCode(cc.id); if (e.key === 'Escape') setEditingCityId(null); }}
                            data-testid={`edit-city-code-input-${cc.city}`}
                          />
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleEditCityCode(cc.id)} disabled={cityCodeSaving} data-testid={`save-city-code-${cc.city}`}>
                            <Check className="w-3.5 h-3.5 text-green-600" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditingCityId(null)} data-testid={`cancel-city-code-${cc.city}`}>
                            <X className="w-3.5 h-3.5 text-red-500" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <Badge variant="outline" className="font-mono">{cc.code}</Badge>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditingCityId(cc.id); setEditCityCodeValue(cc.code); }} data-testid={`edit-city-code-btn-${cc.city}`}>
                            <Pencil className="w-3 h-3 text-muted-foreground" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Add New City */}
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Add New City</p>
              <div className="flex gap-2">
                <Input
                  placeholder="City name"
                  value={newCityName}
                  onChange={(e) => setNewCityName(e.target.value.toUpperCase())}
                  className="flex-1"
                />
                <Input
                  placeholder="Code"
                  value={newCityCode}
                  onChange={(e) => setNewCityCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2))}
                  className="w-20 uppercase font-mono"
                  maxLength={2}
                />
                <Button onClick={handleAddCityCode} size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {newCityName && !newCityCode && (
                <p className="text-xs text-muted-foreground mt-1">
                  Suggested code: {newCityName.substring(0, 2)}
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCityManager(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default ClientForm;
